import { useState, useEffect, useCallback } from "react";
import axios from '../../../api/apiClient';

import {
  CreateTableNew as CreateTable,
  SearchBar,
  Button,
  GeneralMessage,
  useGeneralMessage
} from '../../../common/APICommon';
import { SetAuditTrail, SetAuditTrailBulk } from '../../Reports/AuditTrailReportEmployeeSetup';
import {
  PDFModal as PDFViewModal,
  ImageViewModal,
  ConfirmationModal
} from '../../../common/Modal';
import { token, domain } from '../../Security';
import { LoadingSpinner } from '../../../common/Loading';

import { KPICodesTab } from "./tabs/KPICodesTabFld/KPICodesTab";
import { LegendDetailsTab } from "./tabs/LegendDetailsTabFld/LegendDetailsTab";
// icons
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteForever';

function KpiLegend({ user, isHideSideMenu }) {
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  // Table headers
  var headers = [
    "ID",
    "Description",
    "Number of Codes",
    "Is Active?",
    "Actions"
  ];

  var accessors = [
    "id",
    "description",
    "codeCount",
    "isActive",
    "actions"
  ];

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [data, setData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);
  const [filterData, setFilterData] = useState([]);

  // Form state
  const [form, setForm] = useState({
    description: "",
    isActive: true,
    performanceRatingDefinitions: [] // Array for multiple performanceRatingDefinitions
  });

  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteForm, setShowDeleteForm] = useState(false);

  const apiUrl = domain + "/api/PerformanceRatingLegendGroup";

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Transform data to include code count
      const transformedData = response.data.map(item => ({
        ...item,
        codeCount: item.performanceRatingDefinitions ? item.performanceRatingDefinitions.length : 0
      }));

      console.log({data:response.data})
      setData(transformedData);
      setFilterData(transformedData);
    } catch (error) {
      console.error('Error fetching data:', error);
      showGeneralMessage("Failed to load KPI Legends", false);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilter = (value) => {
    setCurrentPage(1);
    const lowercaseValue = value.toLowerCase();

    if (!value) {
      setData(filterData);
      return;
    }

    const res = filterData.filter((f) => {
      const match1 = f.description?.toString().toLowerCase().includes(lowercaseValue);
      const match2 = f.id?.toString().startsWith(lowercaseValue);
      return match1 || match2;
    });

    setData(res);
  };

  const resetLocalVariables = () => {
    setForm({
      description: "",
      isActive: true,
      performanceRatingDefinitions: []
    });
    setSelectedData(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.description.trim()) {
      showGeneralMessage("Please enter a description", false);
      return;
    }

    // Validate performanceRatingDefinitions
    if (form.performanceRatingDefinitions.length === 0) {
      showGeneralMessage("Please add at least one KPI code", false);
      return;
    }

    const apiData = {
      description: form.description.trim(),
      isActive: form.isActive,
      performanceRatingDefinitions: form.performanceRatingDefinitions.map(performanceRatingDefinition => ({
        code: performanceRatingDefinition.code.trim(),
        description: performanceRatingDefinition.description.trim(),
        rating: parseInt(performanceRatingDefinition.rating) || 0

      }))
    };

  
    try {
      setLoadingRequest(true);
      await axios.post(apiUrl, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      await SetAuditTrail({
        setupArea: "KPILegend",
        actionTaken: "insert",
        field: "Add",
        newValue: `${apiData.description}`,
        oldValue: "-",
        dateSaved: new Date(),
        savedById: user.id
      });

      await fetchData();
      showGeneralMessage("KPI Legend created successfully!", true);
      closeAddHandleClick();
    } catch (error) {
      console.error('Error creating data:', error.response || error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to create KPI Legend";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const handleUpdate = async (event) => {
    event.preventDefault();

    if (!selectedData?.id) {
      showGeneralMessage("No record selected for update", false);
      return;
    }

    if (!form.description.trim()) {
      showGeneralMessage("Please enter a description", false);
      return;
    }

    // Validate performanceRatingDefinitions
    if (form.performanceRatingDefinitions.length === 0) {
      showGeneralMessage("Please add at least one KPI code", false);
      return;
    }

    const apiData = {
      description: form.description.trim(),
      isActive: form.isActive,
      performanceRatingDefinitions: form.performanceRatingDefinitions.map(performanceRatingDefinition => ({
        id: performanceRatingDefinition.id,
        code: performanceRatingDefinition.code.trim(),
        description: performanceRatingDefinition.description.trim(),
        rating: parseInt(performanceRatingDefinition.rating) || 0,
        performanceRatingsLegendId: selectedData.id
      }))
    };

    try {
      setLoadingRequest(true);
      await axios.put(`${apiUrl}/${selectedData.id}`, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      let auditInfo = [];

      if (apiData.description !== selectedData.description) {
        auditInfo.push({
          setupArea: "KPILegend",
          actionTaken: "update",
          field: "Description",
          newValue: `${apiData.description}`,
          oldValue: `${selectedData.description}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if (apiData.isActive !== selectedData.isActive) {
        auditInfo.push({
          setupArea: "KPILegend",
          actionTaken: "update",
          field: "Is Active?",
          newValue: `${apiData.isActive ? "Active" : "Inactive"}`,
          oldValue: `${selectedData.isActive ? "Active" : "Inactive"}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      // Check for performanceRatingDefinitions changes
      const oldPerformanceRatingDefinitions = selectedData.performanceRatingDefinitions || [];
      const newPerformanceRatingDefinitions = apiData.performanceRatingDefinitions || [];

      // Simple check for performanceRatingDefinitions changes
      const codesChanged = oldPerformanceRatingDefinitions.length !== newPerformanceRatingDefinitions.length ||
        JSON.stringify(oldPerformanceRatingDefinitions.map(p => ({ code: p.code, description: p.description }))) !==
        JSON.stringify(newPerformanceRatingDefinitions.map(p => ({ code: p.code, description: p.description })));

      if (codesChanged) {
        auditInfo.push({
          setupArea: "KPILegend",
          actionTaken: "update",
          field: "KPI Codes",
          newValue: `Updated ${newPerformanceRatingDefinitions.length} performanceRatingDefinitions`,
          oldValue: `${oldPerformanceRatingDefinitions.length} performanceRatingDefinitions`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if (auditInfo.length > 0) {
        await SetAuditTrailBulk({ data: auditInfo });
      }

      await fetchData();
      showGeneralMessage("KPI Legend updated successfully!", true);
      closeEditHandleClick();
    } catch (error) {
      console.error('Error updating data:', error.response || error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to update KPI Legend";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedData?.id) {
      showGeneralMessage("No record selected for deletion", false);
      return;
    }

    try {
      setLoadingRequest(true);
      await axios.delete(`${apiUrl}/${selectedData.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      await SetAuditTrail({
        setupArea: "KPILegend",
        actionTaken: "delete",
        field: "Delete",
        newValue: `${selectedData.description}`,
        oldValue: "-",
        dateSaved: new Date(),
        savedById: user.id
      });

      await fetchData();
      showGeneralMessage("KPI Legend deleted successfully!", true);
      closeDeleteHandleClick();
    } catch (error) {
      console.error('Error deleting data:', error.response || error);
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to delete KPI Legend";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const editHandleClick = (data) => {
    setSelectedData(data);
    setForm({
      description: data.description || "",
      isActive: data.isActive !== undefined ? data.isActive : true,
      performanceRatingDefinitions: data.performanceRatingDefinitions ? data.performanceRatingDefinitions.map(performanceRatingDefinition => ({
        id: performanceRatingDefinition.id,
        code: performanceRatingDefinition.code || "",
        description: performanceRatingDefinition.description || "",
        rating: performanceRatingDefinition.rating || "" // ADD THIS LINE
      })) : []
    });
    openEditHandleClick();
  };

  const deleteHandleClick = (data) => {
    setSelectedData(data);
    setShowDeleteForm(true);
  };

  const openAddHandleClick = () => {
    setShowAddForm(true);
  };

  const closeAddHandleClick = () => {
    setShowAddForm(false);
    resetLocalVariables();
  };

  const openEditHandleClick = () => {
    setShowEditForm(true);
  };

  const closeEditHandleClick = () => {
    setShowEditForm(false);
    resetLocalVariables();
  };

  const closeDeleteHandleClick = () => {
    setShowDeleteForm(false);
    setSelectedData(null);
  };

  // Custom cell renderer
  const customCellRenderer = (row, accessor) => {
    if (accessor === "isActive") {
      const isActive = row[accessor];
      if (isActive === true || isActive === "true" || isActive === "True") {
        return (
          <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-green-100 text-green-800 text-sm font-medium">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 mr-2"></div>
            Active
          </div>
        );
      } else {
        return (
          <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-red-100 text-red-800 text-sm font-medium">
            <div className="w-1.5 h-1.5 rounded-full bg-red-500 mr-2"></div>
            Inactive
          </div>
        );
      }
    }

    if (accessor === "codeCount") {
      return (
        <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
          {row.codeCount} performanceRatingDefinitions
        </div>
      );
    }

    // Default rendering
    const value = row[accessor] ?? "-";
    return (
      <span className="text-gray-700 px-2">
        {value}
      </span>
    );
  };

  const actions = [
    {
      name: "Edit",
      tooltip: "edit",
      icon: <EditIcon fontSize="small" />,
      className: "bg-green-500 hover:bg-green-600 flex items-center",
      function: editHandleClick,
      visible: true,
    },
    {
      name: "Delete",
      tooltip: "delete",
      icon: <DeleteIcon fontSize="small" />,
      className: "bg-red-500 hover:bg-red-600 flex items-center",
      function: deleteHandleClick,
      visible: true,
    },
  ];

  return (
    <div>
      <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
        <div className="LeftSpace"></div>
        <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
          <div className="MainteInfo shadow-xl">
            <div className="mainteHeader">
              <div className='sub-title row'>
                <span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Performance Management >"} </span>
                <span style={{ borderBottom: "1px solid black" }}>{"KPI Legends"}</span>
              </div>
              <h3>KPI Legends</h3>
            </div>
            <div className='space-y-4 p-4'>
              <SearchBar handleFilter={handleFilter} />

              <Button
                icon={<AddIcon />}
                name={"Add KPI Legend"}
                className={"bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"}
                onClick={openAddHandleClick}
              />

              <CreateTable
                tableName={"KPILegends"}
                headers={headers}
                accessors={accessors}
                data={data}
                actions={actions}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                itemsPerPage={10}
                totalItems={data?.length}
                // customCellRenderer={customCellRenderer}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add Form */}
      <KPILegendForm
        isOpen={showAddForm}
        onClose={closeAddHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleSubmit}
        modalName={"Add KPI Legend"}
        mode="add"
        showGeneralMessage={showGeneralMessage}
        user={user}
      />

      {/* Edit Form */}
      <KPILegendForm
        isOpen={showEditForm}
        onClose={closeEditHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleUpdate}
        modalName={"Update KPI Legend"}
        mode="edit"
        showGeneralMessage={showGeneralMessage}
        user={user}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        visible={showDeleteForm}
        onClose={closeDeleteHandleClick}
        onConfirm={handleDelete}
        title="Delete KPI Legend"
        message="Are you sure you want to delete this KPI Legend? This will also delete all associated KPI performanceRatingDefinitions."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={loadingRequest}
      />

      <GeneralMessage
        form={showMessage}
        message={message}
        isSuccess={isSuccess}
      />

      <LoadingSpinner loading={loadingRequest || loading} />
    </div>
  );
}

// Main Form Component with Tabs
function KPILegendForm({
  isOpen,
  modalName,
  onClose,
  form,
  setForm,
  handleSubmit,
  mode = "add",
  showGeneralMessage,
  user
}) {
  const [activeTab, setActiveTab] = useState("legend");
  const [errorState, setErrorState] = useState({
    description: false
  });

  const submit = (e) => {
    e.preventDefault();

    if (!validateKPICodesForm(form, showGeneralMessage)) {
      return;
    }

    handleSubmit(e);
  };

  const handleClose = () => {
    onClose();
    setErrorState({ description: false });
    setActiveTab("legend");
  };

  const handleInputChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Code management functions - UPDATED TO HANDLE RATING
  const handleAddCode = (codeData = null) => {
    if (codeData) {
      // When called from KPICodesTab with complete data
      setForm(prev => ({
        ...prev,
        performanceRatingDefinitions: [...prev.performanceRatingDefinitions, {
          code: codeData.code || "",
          description: codeData.description || "",
          rating: codeData.rating || ""
        }]
      }));
    } else {
      // Fallback for compatibility (add empty code)
      setForm(prev => ({
        ...prev,
        performanceRatingDefinitions: [...prev.performanceRatingDefinitions, {
          code: "",
          description: "",
          rating: ""
        }]
      }));
    }
  };

  const handleRemoveCode = (index) => {
    setForm(prev => ({
      ...prev,
      performanceRatingDefinitions: prev.performanceRatingDefinitions.filter((_, i) => i !== index)
    }));
  };

  const handleCodeChange = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      performanceRatingDefinitions: prev.performanceRatingDefinitions.map((code, i) =>
        i === index ? { ...code, [field]: value } : code
      )
    }));
  };

  // Not needed for the new pattern, but keeping for compatibility
  const handleSaveCode = (index, updatedCode) => {
    setForm(prev => ({
      ...prev,
      performanceRatingDefinitions: prev.performanceRatingDefinitions.map((performanceRatingDefinition, i) =>
        i === index ? updatedCode : performanceRatingDefinition
      )
    }));
  };

  if (!isOpen) return null;

  const isEditMode = mode === "edit";

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[10000] flex items-center justify-center overflow-auto bg-black/50 p-3 text-gray-700"
      >
        <div className="relative flex h-auto max-h-[90vh] w-full flex-col rounded-lg bg-gray-50 shadow-xl md:w-[90%] lg:w-[80%] xl:w-[70%]">

          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-2 shadow-sm">
            <h2 className="flex items-center space-x-2 text-xl font-semibold text-gray-800">
              <span className="text-xl text-gray-600">{modalName}</span>
            </h2>
            <button
              onClick={handleClose}
              className="border-none bg-transparent p-1 text-gray-400 transition-colors hover:text-gray-700"
              aria-label="Close modal"
            >
              <CloseIcon fontSize="medium" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 bg-white pt-3">
            <div className="flex space-x-1 px-6">
              <button
                onClick={() => setActiveTab("legend")}
                className={`px-4 py-3 text-sm font-medium rounded-t-lg transition-colors border-b-2 ${activeTab === "legend"
                  ? "border-blue-500 text-blue-600 bg-blue-50"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Legend Details
              </button>
              <button
                onClick={() => setActiveTab("performanceRatingDefinitions")}
                className={`px-4 py-3 text-sm font-medium rounded-t-lg transition-colors border-b-2 ${activeTab === "performanceRatingDefinitions"
                  ? "border-blue-500 text-blue-600 bg-blue-50"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
              >
                KPI Codes ({form.performanceRatingDefinitions?.length || 0})
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === "legend" && (
              <LegendDetailsTab
                form={form}
                errorState={errorState}
                handleInputChange={handleInputChange}
              />
            )}

            {activeTab === "performanceRatingDefinitions" && (
              <KPICodesTab
                form={form}
                handleAddCode={handleAddCode} // Pass the updated function
                handleRemoveCode={handleRemoveCode}
                handleCodeChange={handleCodeChange}
                handleSaveCode={handleSaveCode}
              />
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between space-x-2 rounded-b-lg border-t border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center text-sm text-gray-600">
              {activeTab === "performanceRatingDefinitions" && (
                <span>
                  {form.performanceRatingDefinitions?.length || 0} KPI code(s) added
                </span>
              )}
            </div>

            <div className="flex space-x-2">
              <Button 
                name={"Cancel"}
                className={"cursor-pointer rounded border-none bg-gray-400 px-4 py-2 text-white transition-colors hover:bg-gray-500"}
                onClick={handleClose}
              />
              <Button 
                name={isEditMode ? "Update" : "Save"}
                className={"cursor-pointer rounded border-none bg-blue-500 px-4 py-2 text-white transition-colors hover:bg-blue-600"}
                onClick={submit}
              />
            </div>
          </div>

        </div>
      </div>
    </>
  );
}

const validateKPICodesForm = (form, showGeneralMessage) => {
  // Validate main form
  if (!form.description?.trim()) {
    showGeneralMessage("Please enter a description for the KPI Legend", false);
    return false;
  }

  // Validate performanceRatingDefinitions
  if (form.performanceRatingDefinitions.length === 0) {
    showGeneralMessage("Please add at least one KPI code", false);
    return false;
  }

  // Validate each code
  const invalidCodes = form.performanceRatingDefinitions.filter((performanceRatingDefinition, index) => {
    return !performanceRatingDefinition.code?.trim() || !performanceRatingDefinition.description?.trim() || !performanceRatingDefinition.rating || isNaN(performanceRatingDefinition.rating);
  });

  if (invalidCodes.length > 0) {
    showGeneralMessage(`Please fill all required fields for ${invalidCodes.length} code(s)`, false);
    return false;
  }

  // Check for duplicate performanceRatingDefinitions
  const performanceRatingDefinitions = form.performanceRatingDefinitions.map(performanceRatingDefinition => performanceRatingDefinition.code.trim().toLowerCase());
  const uniqueCodes = [...new Set(performanceRatingDefinitions)];
  if (performanceRatingDefinitions.length !== uniqueCodes.length) {
    showGeneralMessage("Duplicate performanceRatingDefinitions detected. Please use unique performanceRatingDefinitions.", false);
    return false;
  }

  return true;
};

export default KpiLegend;